/** Local tangent for admin sim ↔ Leaflet. Mean Earth radius matches pose_filter. */

export const EARTH_M = 6_371_000
export const SIM_CAM = "moon-scan:sim-cam"
export const SIM_CAM_PLACE = "moon-scan:sim-cam-place"
export const SIM_PIN = "moon-scan:sim-pin"

/** Sim: +X east, +Z south; yaw 0 looks −Z (north). */
export function simToEn(x, z) {
  return {east: x, north: -z}
}

export function enToSim(east, north) {
  return {x: east, z: -north}
}

export function yawToHeadingDeg(yawRad) {
  const deg = ((-yawRad * 180) / Math.PI) % 360
  if (deg > 0) return deg
  if (deg < 0) return deg + 360
  return 0
}

/** Compass degrees clockwise from north for a ground ENU direction. */
export function headingDegFromEn(east, north) {
  const deg = (Math.atan2(east, north) * 180) / Math.PI
  if (deg > 0) return deg
  if (deg < 0) return deg + 360
  return 0
}

/**
 * Ground heading of the sim camera. Matches Three.js YXZ local −Z
 * (same as `camera.rotation.set(pitch, yaw, 0)` in ARSim / live AR
 * `rotation.set(-pitch, -heading, 0)`).
 */
export function headingDegFromSimLook(yawRad, pitchRad = 0) {
  const cp = Math.cos(pitchRad)
  const east = -cp * Math.sin(yawRad)
  const north = cp * Math.cos(yawRad)
  return headingDegFromEn(east, north)
}

export function enToLatLng(originLat, originLng, east, north) {
  const lat0 = (originLat * Math.PI) / 180
  const cos = Math.cos(lat0)
  return {
    lat: originLat + (north / EARTH_M) * (180 / Math.PI),
    lng: originLng + (east / (EARTH_M * (Math.abs(cos) < 1e-8 ? 1e-8 : cos))) * (180 / Math.PI),
  }
}

export function latLngToEn(originLat, originLng, lat, lng) {
  const lat0 = (originLat * Math.PI) / 180
  const east = EARTH_M * Math.cos(lat0) * (((lng - originLng) * Math.PI) / 180)
  const north = EARTH_M * (((lat - originLat) * Math.PI) / 180)
  return {east, north}
}

export function emitSimCam(detail) {
  window.dispatchEvent(new CustomEvent(SIM_CAM, {detail}))
}

export function emitSimCamPlace(detail) {
  window.dispatchEvent(new CustomEvent(SIM_CAM_PLACE, {detail}))
}

export function emitSimPin(lat, lng) {
  window.dispatchEvent(new CustomEvent(SIM_PIN, {detail: {lat, lng}}))
}

export const MAP_TOOLS = ["pan", "pin", "walk"]
export const COARSE_MQ = "(pointer: coarse), (hover: none)"

const toolByEl = new WeakMap()

export function coarsePointer(matchMedia) {
  const mq =
    matchMedia ??
    (typeof globalThis.matchMedia === "function" ? globalThis.matchMedia.bind(globalThis) : null)
  if (typeof mq !== "function") return false
  try {
    return Boolean(mq(COARSE_MQ)?.matches)
  } catch {
    return false
  }
}

export function setMapTool(el, tool) {
  if (!el) return
  if (tool == null || tool === "") {
    toolByEl.delete(el)
    return
  }
  if (!MAP_TOOLS.includes(tool)) return
  toolByEl.set(el, tool)
}

export function getMapTool(el) {
  return el ? (toolByEl.get(el) ?? null) : null
}

/** Map click must not move the event pin while the sim camera is the target. */
export function mapClickMovesPin(event, {camDragging, suppressClick} = {}) {
  if (camDragging || suppressClick) return false
  const map = event?.target
  const container = map && typeof map.getContainer === "function" ? map.getContainer() : null
  const tool = getMapTool(container)
  if (tool === "pan" || tool === "walk") return false
  const target = event?.originalEvent?.target
  if (target && typeof target.closest === "function" && target.closest(".pin-sim-cam")) {
    return false
  }
  return true
}

/** Walk-tool tap: ENU of the tap relative to the pin. Null if the click has no latlng. */
export function walkPlaceFromClick(event, originLat, originLng) {
  const lat = event?.latlng?.lat
  const lng = event?.latlng?.lng
  if (!Number.isFinite(lat) || !Number.isFinite(lng)) return null
  if (!Number.isFinite(originLat) || !Number.isFinite(originLng)) return null
  return latLngToEn(originLat, originLng, lat, lng)
}
